import { productImg, materialSpecs } from "@/data/products";
import { goDownload } from "@/lib/scroll";
import { GALLERY } from "@/constants/testIds";
import { ArrowUpRight, Layers, Boxes, Sparkles, Clock } from "lucide-react";

// A premium studio-style product tile with a Material Specs block.
export const ProductCard = ({ product, size = "md" }) => {
  const plateH =
    size === "lg" ? "h-64 sm:h-72 lg:h-80" : size === "sm" ? "h-40" : "h-52";
  const specs = materialSpecs(product);

  return (
    <div
      data-testid={GALLERY.tile}
      data-product={product.slug}
      className="group relative flex flex-col overflow-hidden rounded-2xl border border-border/70 bg-card/70 backdrop-blur-md shadow-[var(--shadow-elev-1)] transition-[transform,border-color,box-shadow] duration-300 hover:-translate-y-1 hover:border-[rgba(0,229,255,0.4)] hover:shadow-[var(--shadow-elev-2)]"
    >
      {/* Studio plate */}
      <div
        className={`relative ${plateH} w-full overflow-hidden`}
        style={{
          background:
            "radial-gradient(circle at 50% 32%, #f6f8fc 0%, #e7ecf3 55%, #d7dde7 100%)",
        }}
      >
        <img
          src={productImg(product)}
          alt={product.name}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-contain p-4 transition-transform duration-500 ease-out group-hover:scale-105"
        />
        {product.tag && (
          <span className="absolute left-3 top-3 rounded-full border border-[rgba(0,229,255,0.5)] bg-black/70 px-2.5 py-1 font-mono text-[10px] uppercase tracking-wider text-neo-cyan">
            {product.tag}
          </span>
        )}
        <span className="absolute right-3 top-3 rounded-full bg-black/70 px-2.5 py-1 font-mono text-[11px] font-semibold text-white">
          ${product.price}
        </span>
      </div>

      {/* Body */}
      <div className="flex flex-1 flex-col gap-3 p-4">
        <div>
          <h3 className="font-display text-base font-semibold leading-tight text-foreground">
            {product.name}
          </h3>
          <span className="mt-1 inline-block rounded-md bg-secondary/60 px-2 py-0.5 text-[11px] text-muted-foreground">
            {product.category}
          </span>
        </div>

        {/* Material Specs */}
        <div className="rounded-xl border border-border/60 bg-secondary/30 p-2.5">
          <div className="mb-2 flex items-center gap-1.5">
            <Sparkles className="h-3 w-3 text-neo-cyan" />
            <span className="font-mono text-[9px] uppercase tracking-wider text-muted-foreground">
              Material Specs
            </span>
          </div>
          <dl className="grid grid-cols-2 gap-x-3 gap-y-2">
            <Spec icon={Boxes} label="Material" value={specs.material} />
            <Spec icon={Layers} label="Layer res." value={specs.resolution} />
            <Spec icon={Sparkles} label="Finish" value={specs.finish} />
            <Spec icon={Clock} label="Ships in" value={specs.lead} />
          </dl>
        </div>

        <button
          type="button"
          onClick={goDownload}
          aria-label={`Get ${product.name} on the app`}
          className="mt-auto inline-flex items-center justify-center gap-1.5 rounded-lg border border-[rgba(0,229,255,0.35)] bg-[rgba(0,229,255,0.12)] px-3 py-2 text-sm font-medium text-foreground transition-colors duration-200 hover:bg-[rgba(0,229,255,0.2)]"
        >
          Get it on the app <ArrowUpRight className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
};

const Spec = ({ icon: Icon, label, value }) => (
  <div className="flex items-start gap-1.5">
    <Icon className="mt-0.5 h-3 w-3 shrink-0 text-muted-foreground" />
    <div className="leading-tight">
      <dt className="font-mono text-[9px] uppercase tracking-wide text-muted-foreground">
        {label}
      </dt>
      <dd className="font-mono text-[11px] text-foreground">{value}</dd>
    </div>
  </div>
);
